import { useState } from "react";
import { Send } from "lucide-react";
import { Button } from "./kd";
import { StatusBadge, STATUS_ICON } from "./Badges";
import { fmtDateTime, STATUS } from "../lib/format";
import { errMsg } from "../lib/api";
import { toast } from "sonner";
import { CHAT_AKTIONEN, KiCtx, KiMenu, KiPreview, MicButton, useKi, useKiText } from "./Ki";
import { SnippetTextarea } from "./Snippets";

export function Timeline({ items }: { items: any[] }) {
  return (
    <ol className="relative ml-3 border-l-2 border-border" data-testid="timeline">
      {items.map((v, i) => {
        const Icon = STATUS_ICON(v.zu_status);
        return (
          <li key={i} className="mb-5 ml-5 last:mb-0">
            <span className="absolute -left-[13px] flex h-6 w-6 items-center justify-center rounded-full border-2 border-surface bg-primary-light text-primary">
              <Icon className="h-3.5 w-3.5" aria-hidden />
            </span>
            <p className="font-medium">{STATUS[v.zu_status]}</p>
            <p className="text-sm text-text-muted">{fmtDateTime(v.erstellt_am)}{v.benutzer ? ` · ${v.benutzer}` : ""}</p>
            {v.kommentar && <p className="mt-1 whitespace-pre-wrap rounded-md bg-bg p-2 text-sm">{v.kommentar}</p>}
          </li>
        );
      })}
    </ol>
  );
}

export function Chat({ messages, me, onSend, ki }: { messages: any[]; me: "PRAXIS" | "UEBERWEISER"; onSend: (t: string) => Promise<any>; ki?: KiCtx }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const flags = useKi(ki || null);
  const kt = useKiText(ki || {});
  const aktionen = me === "PRAXIS" ? { ...CHAT_AKTIONEN, einfach: "In einfache Sprache (für Patienten)" } : CHAT_AKTIONEN;
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await onSend(text.trim()); setText(""); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="chat">
      <div className="mb-3 max-h-[420px] space-y-3 overflow-y-auto" data-testid="chat-messages">
        {messages.length === 0 && <p className="text-sm text-text-muted">Noch keine Nachrichten.</p>}
        {messages.map((m) => {
          const mine = m.absender === me;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-xl px-3 py-2 ${mine ? "bg-primary-light" : "bg-bg border border-border"}`}>
                <p className="text-sm text-text-muted">{m.absender === "PRAXIS" ? (m.benutzer || "Fachpraxis") : "Überweiser"} · {fmtDateTime(m.erstellt_am)}</p>
                <p className="whitespace-pre-wrap break-words">{m.text}</p>
              </div>
            </div>
          );
        })}
      </div>
      <label htmlFor="chat-input" className="kd-label">Nachricht</label>
      <SnippetTextarea id="chat-input" rows={3} maxLength={5000} value={text} onValue={setText} data-testid="chat-input" />
      {kt.suggestion && <KiPreview text={kt.suggestion.text} ctx={ki || {}} aktion={kt.suggestion.aktion} onAccept={setText} onClose={kt.clear} acceptLabel="In Eingabefeld übernehmen" />}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Button onClick={send} loading={busy} disabled={!text.trim()} data-testid="chat-send-button"><Send className="h-4 w-4" aria-hidden />Senden</Button>
        {flags.sprache && ki && <MicButton ctx={ki} onText={(t) => setText((x) => (x ? `${x} ${t}` : t))} testid="chat-mic" />}
        {flags.assistent && <KiMenu aktionen={aktionen} busy={kt.busy} onPick={(a) => kt.ask(a, text)} />}
      </div>
    </div>
  );
}

export { StatusBadge };
